import { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  primary:   'bg-broker text-white hover:bg-broker-light active:scale-[0.98]',
  secondary: 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text border border-gray-200 dark:border-dark-border hover:bg-gray-200 dark:hover:bg-slate-600 active:scale-[0.98]',
  outline:   'bg-white dark:bg-dark-card text-broker border border-broker hover:bg-broker-bg dark:hover:bg-slate-700 active:scale-[0.98]',
  ghost:     'bg-transparent text-gray-600 dark:text-dark-text-muted border border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-slate-700 active:scale-[0.98]',
  danger:    'bg-danger text-white hover:bg-red-600 active:scale-[0.98]',
};

const sizeClasses: Record<Size, string> = {
  sm:  'px-3 py-1.5 text-xs rounded-lg',
  md:  'px-4 py-2.5 text-sm rounded-lg',
  lg:  'px-6 py-3 text-base rounded-xl',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  className = '',
  ...props
}: ButtonProps) {
  return (
    <button
      disabled={disabled || loading}
      className={[
        'inline-flex items-center justify-center gap-2',
        'font-semibold transition-all duration-150',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? 'w-full' : '',
        className,
      ].join(' ')}
      {...props}
    >
      {loading && (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {children}
    </button>
  );
}
